import {noop} from 'lodash';
import Image from 'next/image';
import {useState} from 'react';
import {useTranslations} from 'next-intl';
import {GoogleOAuthProvider} from '@react-oauth/google';
import FacebookLoginButton, {isFacebookApp} from './FacebookLoginButton';
import SocialButton from 'src/pages/auth/SocialButton';
import GoogleLoginButton from './GoogleLoginButton';
import SignUpForm from 'src/pages/auth/SignUpForm';
import LoginForm from 'src/pages/auth/LoginForm';
import BaseButton from './BaseButton';

type Props = {
  onSignInSuccess?: (user: User) => void;
  signUpMode: boolean;
  setSignUpMode: (signUpMode: boolean) => void;
  collapsedEmail?: boolean;
  showApproveTermsStep?: boolean;
  brandStyle: BrandStyle;
};

const SignIn = ({
  onSignInSuccess = noop,
  signUpMode,
  setSignUpMode,
  collapsedEmail = false,
  showApproveTermsStep,
  brandStyle
}: Props) => {
  const t = useTranslations();

  const [emailSigninActive, setEmailSigninActive] = useState(!collapsedEmail);
  const [socialErrorMessage, setSocialErrorMessage] = useState('');

  return (
    <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}>
      <div className="flex flex-col gap-6 items-center w-full max-w-[460px]">
        {emailSigninActive ? (
          signUpMode ? (
            <>
              <SignUpForm
                onSignInSuccess={onSignInSuccess}
                showApproveTermsStep={showApproveTermsStep}
                brandStyle={brandStyle}
              />

              <div className="flex flex-wrap">
                <BaseButton
                  variant="text"
                  onClick={() => setSignUpMode(false)}
                  label={t('auth.already have account')}
                />
              </div>
            </>
          ) : (
            <>
              <LoginForm onSignInSuccess={onSignInSuccess} brandStyle={brandStyle} />

              <div className="flex flex-wrap">
                <BaseButton variant="text" onClick={() => setSignUpMode(true)} label={t('auth.sign up')} />
                <BaseButton variant="text" href="/reset-password" label={t('auth.forgot password')} />
              </div>
            </>
          )
        ) : null}

        <div className="flex flex-col w-full p-4 gap-4">
          {isFacebookApp() ? null : (
            <GoogleLoginButton onSignInSuccess={onSignInSuccess} setErrorMessage={setSocialErrorMessage} />
          )}

          <FacebookLoginButton onSignInSuccess={onSignInSuccess} setErrorMessage={setSocialErrorMessage} />

          {collapsedEmail && !emailSigninActive ? (
            <SocialButton
              ProviderLogo={<Image src="/icons/emailSigninIcon.svg" alt="Email" width={30} height={30} />}
              text={t('auth.continue with email')}
              onClick={() => setEmailSigninActive(true)}
            />
          ) : null}

          {socialErrorMessage ? <p className="w-full mb-4 text-sm text-[--error-color]">{socialErrorMessage}</p> : null}
        </div>
      </div>
    </GoogleOAuthProvider>
  );
};

export default SignIn;
